import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  XCircle,
  Bookmark,
  BookmarkCheck,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  getStudyQuestions,
  submitAnswer,
  toggleBookmark,
  trackEvent,
} from "@/lib/api";
import { STUDY } from "@/constants/testIds";
import ProtectedContent from "@/components/study/ProtectedContent";
import TutorPanel from "@/components/study/TutorPanel";
import QuestionReportButton from "@/components/study/QuestionReportButton";

const LETTERS = ["A", "B", "C", "D"];

export default function StudyRunner({ mode = "all", portion, topic, limit, emptyText }) {
  const params = { mode };
  if (portion) params.portion = portion;
  if (topic) params.topic = topic;
  if (limit) params.limit = limit;

  const { data: questions, isLoading, refetch } = useQuery({
    queryKey: ["study-q", mode, portion || "", topic || "", limit || ""],
    queryFn: () => getStudyQuestions(params),
  });

  const [i, setI] = useState(0);
  const [sel, setSel] = useState(null);
  const [result, setResult] = useState(null);
  const [bm, setBm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const list = questions || [];
  const q = list[i];
  const questionId = q?.id;
  const questionIsBookmarked = Boolean(q?.is_bookmarked);

  useEffect(() => {
    if (mode === "missed") trackEvent("missed_review_started");
  }, [mode]);

  useEffect(() => {
    if (questionId) {
      setSel(null);
      setResult(null);
      setBm(questionIsBookmarked);
    }
  }, [questionId, questionIsBookmarked]);

  if (isLoading) {
    return (
      <p className="py-10 text-center text-sm text-[color:rgba(11,18,32,0.6)]">
        Loading questions...
      </p>
    );
  }

  if (!q) {
    return (
      <div
        data-testid={STUDY.emptyState}
        className="rounded-2xl border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-14 text-center"
      >
        <p className="text-base font-semibold text-[var(--ibp-ink)]">
          {emptyText || "No questions here yet."}
        </p>
      </div>
    );
  }

  const submit = async () => {
    if (!sel) return;
    setSubmitting(true);
    try {
      const r = await submitAnswer(q.id, sel, mode);
      setResult(r);
      trackEvent("question_answered", { question_id: q.id, is_correct: r.is_correct });
      trackEvent("explanation_viewed", { question_id: q.id });
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (i < list.length - 1) {
      setI(i + 1);
    } else {
      toast.success("Set complete");
      setI(0);
      refetch();
    }
  };

  const onBookmark = async () => {
    const nb = !bm;
    setBm(nb);
    try {
      await toggleBookmark(q.id, nb);
      trackEvent("question_bookmarked", { question_id: q.id, bookmarked: nb });
    } catch (e) {
      setBm(!nb);
    }
  };

  return (
    <ProtectedContent
      data-testid={STUDY.questionCard}
      className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-8"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-[var(--ibp-ink)]">
          Question {i + 1} of {list.length}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-[var(--ibp-navy-950)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
            {q.topic}
          </span>
          {result ? (
            <span className="rounded-full bg-[var(--ibp-offwhite)] px-2.5 py-1 text-[11px] font-medium capitalize text-[color:rgba(11,18,32,0.7)]">
              {q.difficulty}
            </span>
          ) : null}
          <button
            type="button"
            data-testid={STUDY.bookmark}
            onClick={onBookmark}
            aria-label="Bookmark"
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.06)]"
          >
            {bm ? (
              <BookmarkCheck className="h-4 w-4 text-[var(--ibp-gold-500)]" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      <h2 className="mt-5 text-lg font-semibold leading-7 text-[var(--ibp-ink)]">
        {q.question_text}
      </h2>

      <div className="mt-5 space-y-3">
        {LETTERS.map((letter) => {
          const isSel = sel === letter;
          const reveal = Boolean(result);
          const isCorrect = reveal && result.correct_answer === letter;
          const isWrongPick = reveal && isSel && !result.is_correct;
          return (
            <button
              key={letter}
              type="button"
              data-testid={`${STUDY.choice}-${letter}`}
              disabled={reveal}
              onClick={() => setSel(letter)}
              className={[
                "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                isCorrect
                  ? "border-[#15803d] bg-[color:rgba(21,128,61,0.1)]"
                  : isWrongPick
                  ? "border-[#9b2c2c] bg-[color:rgba(155,44,44,0.08)]"
                  : isSel
                  ? "border-[var(--ibp-gold-500)] bg-[color:rgba(214,162,30,0.1)]"
                  : "border-[color:rgba(10,27,51,0.15)] bg-white hover:border-[var(--ibp-navy-950)]",
              ].join(" ")}
            >
              <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-[var(--ibp-offwhite)] text-sm font-bold text-[var(--ibp-ink)]">
                {letter}
              </span>
              <span className="text-sm leading-6 text-[var(--ibp-ink)]">
                {q.choices[letter]}
              </span>
            </button>
          );
        })}
      </div>

      {result && (
        <div
          data-testid={STUDY.explanation}
          className="mt-5 rounded-xl border border-[color:rgba(10,27,51,0.12)] bg-[var(--ibp-offwhite)] p-4"
        >
          <p className="flex items-center gap-2 text-sm font-semibold">
            {result.is_correct ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-[#15803d]" />
                <span className="text-[#14532d]">Correct</span>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-[#9b2c2c]" />
                <span className="text-[#7f1d1d]">
                  Not quite - correct answer is {result.correct_answer}
                </span>
              </>
            )}
          </p>
          <p className="mt-2 text-sm leading-6 text-[color:rgba(11,18,32,0.8)]">
            {result.explanation}
          </p>
          {!result.is_correct && result.wrong_answer_note ? (
            <div
              data-testid="study-wrong-answer-note"
              className="mt-3 rounded-lg border-l-4 border-[#9b2c2c] bg-[color:rgba(155,44,44,0.06)] px-3 py-2.5"
            >
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#9b2c2c]">
                Why {result.selected || "your answer"} is wrong
              </p>
              <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.82)]">
                {result.wrong_answer_note}
              </p>
            </div>
          ) : null}
          {result.source_reference ? (
            <p className="mt-2 text-xs text-[color:rgba(11,18,32,0.55)]">
              Source: {result.source_reference}
            </p>
          ) : null}
        </div>
      )}

      <QuestionReportButton key={`report-${q.id}`} questionId={q.id} />

      {/* Question Coach appears only after the question has been answered. */}
      {result && <TutorPanel key={q.id} questionId={q.id} />}

      <div className="mt-6 flex justify-end">
        {!result ? (
          <button
            type="button"
            data-testid={STUDY.submit}
            onClick={submit}
            disabled={!sel || submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-6 py-3 text-sm font-semibold text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.2)] transition-colors hover:bg-[var(--ibp-primary-hover)] disabled:opacity-60"
          >
            {submitting ? "Checking..." : "Submit answer"}
          </button>
        ) : (
          <button
            type="button"
            data-testid={STUDY.next}
            onClick={next}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--ibp-navy-950)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:opacity-90"
          >
            Next question <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </ProtectedContent>
  );
}
